import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    FiToggleSwitchLabelAlign,
    FiToggleSwitchSliderColorClass,
} from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import { AppTextService } from '@fi-shared/app-text';
import { CheckpointScorings, ICheckpointScoringData } from '@fi-shared/checklist/checklist-consts';
import { ICheckpointScoring } from '@fi-shared/checklist/checklist-v2';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { CheckpointStatusType } from '@fi-shared/checklist';
import { CheckpointEditorFormModelNames, CheckpointScoringType } from '@fi-shared/checklist/checklist-enum';

@Component({
    selector: 'fi-checkpoint-scoring-editor',
    template: `
        <p class="checkpoint-edit-section-name scoring-heading">{{ 'common.scoring' | fiAppText }}</p>
        <p class="scoring-description">{{ 'checklist.scoring_category_description' | fiAppText }}</p>
        
        <fi-toggle-switch
          class="checkpoint-scoring-toggle-switch"
          [switchSliderColorClass]="FiToggleSwitchSliderColorClass.Branded"
          [(value)]="checkpointScoring"
          (valueChange)="onCheckpointScoringChanged()"
          />
        
          @if (checkpointScoring) {
            <div class="checkpoint-scoring-select-category-wrapper">
              <mat-form-field
                appearance="outline"
                class="checkpoint-scoring-select-category custom-arrow-icon"
                >
                <mat-label>{{ 'checklist.select_checkpoint_scoring_category' | fiAppText }}</mat-label>
                <mat-select
                  [ngModel]="checkpointScoringType"
                  (ngModelChange)="onScoringCategorySelected($event)"
                  [name]="CheckpointEditorFormModelNames.Scoring"
                  [required]="checkpointScoring"
                  panelClass="fi-rounded-select-panel"
                  #scoringModel="ngModel"
                  >
                  @for (checkpointScoring of CheckpointScorings; track checkpointScoring) {
                    <mat-option [value]="checkpointScoring.id">
                      {{ getScoringText(checkpointScoring) }}
                    </mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix class="fas fa-caret-down" />
                <mat-error [fiErrorMessage]="scoringModel" />
              </mat-form-field>
              <div class="checkpoint-scoring-info-container">
                {{ 'checklist.checkpoint_score_skipped_description' | fiAppText }}
              </div>
            </div>
          }
        `,
    styleUrls: ['./scoring-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-scoring-editor' },
    providers: [{ provide: FiComponentsContainer, useExisting: FiCheckpointScoringEditorComponent }],
    standalone: false,
})
export class FiCheckpointScoringEditorComponent extends FiComponentsContainer implements OnInit {
    @Input() checkpointScoring: boolean;
    @Output() checkpointScoringChange = new EventEmitter<boolean>();

    @Input() checkpointScoringData: ICheckpointScoring;
    @Output() checkpointScoringDataChange = new EventEmitter<ICheckpointScoring>();

    get checkpointScoringType(): CheckpointScoringType {
        return this.checkpointScoringData?.ScoringType;
    }

    FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;

    constructor(private appTextService: AppTextService) {
        super();
    }

    ngOnInit(): void {}

    onCheckpointScoringChanged() {
        this.checkpointScoringChange.emit(this.checkpointScoring);
        if (!this.checkpointScoring) {
            // reset checkpointScoringData
            this.checkpointScoringData = undefined;
            this.checkpointScoringDataChange.emit(this.checkpointScoringData);
        }
    }

    getScoringText(checkpointScoring: ICheckpointScoringData) {
        const text = this.appTextService.get('checklist.select_checkpoint_scoring_category_text', {
            '{type}': this.appTextService.get(checkpointScoring.textId),
            '{value}': checkpointScoring.defaultOkPts,
        });
        return text;
    }

    onScoringCategorySelected($event: CheckpointScoringType) {
        // https://chainformation.atlassian.net/browse/FI-5685
        // scoring configuration can be more flexible at future
        const selectedScoringCategory = this.CheckpointScorings.find((item) => item.id == $event);
        if (!selectedScoringCategory) {
            throw new Error(
                `unknown scoring type ${this.checkpointScoringData.ScoringType} at onScoringCategorySelected`,
            );
        }
        this.checkpointScoringData = {
            ScoringType: selectedScoringCategory.id,
            OkPts: selectedScoringCategory.defaultOkPts,
            NotOkPts: 0,
            NotFilledOutPts: 0,
        };

        this.checkpointScoringDataChange.emit(this.checkpointScoringData);
    }

    protected readonly CheckpointScorings = CheckpointScorings;
    protected readonly CheckpointEditorFormModelNames = CheckpointEditorFormModelNames;
    protected readonly FiToggleSwitchSliderColorClass = FiToggleSwitchSliderColorClass;
}
